'use client';

import { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import Link from 'next/link';
interface TimelineEvent {
  date: string;
  title: string;
  snippet: string;
  fullText: React.ReactNode;
}

export default function Timeline() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const timelineEvents: TimelineEvent[] = [
    {
      date: 'November 21, 2001',
      title: 'William Hickox is born',
      snippet: "The beginning of Will's story",
      fullText:
        'William Hickox was born November 21, 2001. His story is amazing in so many ways, but who he is and what happened to him is a journey of tragedy, hope, and most importantly an amazing young man and an amazing community. Before March 7th 2023, William, or "Will", was a typical kid, playing Varsity Baseball, Varsity Soccer, an avid member of Chorus, involved in various other organizations, and even started his own business at the age of 17, "Small Job Solutions". He spent much of his time doing what most teenagers do, having fun with his friends, working various jobs and looking forward to going to college. He was in his spring semester of junior year at Bryant University looking forward to graduating and starting a new chapter in his life. And then it happened.',
    },
    {
      date: 'March 7, 2023',
      title: 'Will gets sick at college',
      snippet: 'Flu-like symptoms lead to hospital visit',
      fullText:
        'Will was at college and feeling a bit ill with some flu-like symptoms. When he visited the school nurse for the second time, she advised him to go to a hospital ER as she felt he was dehydrated. He booked a ferry and called Debbie and Andy (Mom and Dad) to fill them in and headed home. From Bryant University to the New London ferry is about an hour drive. During this time he started losing vision in his left eye. He met Debbie at Greenport Hospital at about 8:00 PM, at which time he had lost all vision in his left eye. He was immediately admitted to the ER and was examined and had some tests run. At 11:00 the doctor advised us his condition appeared to be more serious and beyond the expertise of a small town hospital, and that they had contacted Stony Brook University Hospital and an ambulance was on the way.',
    },
    {
      date: 'March 9, 2023',
      title: 'Will stops breathing',
      snippet: 'Intubated and in critical condition',
      fullText:
        'We arrived at Stony Brook University Hospital ER at 2:00 AM. A team of doctors and nurses jumped in like a SWAT team and started blood work, a CAT scan, MRI, and numerous IVs. Only one person is allowed in the ER at a time, so Andy and Debbie took turns to be at his side every minute and gather information from the medical team while the other sat in the waiting room in disbelief. The next 36 hours were the longest 36 hours of our lives. The doctors were baffled, they were testing for so many things but nothing was consistently presenting to anything specific. The next day he was moved to the Neurological Critical Intensive Care Unit. After 36 hours, the doctors told us to go home and get some sleep, he was sedated and resting, and to come back in the morning. We drove home in shock, stunned by it all. We arrived home at 1:00 AM March 9th. An hour later the phone rang and we were told that Will had stopped breathing on his own and they had to intubate him. We left in ten minutes, back to Stony Brook. When we arrived, the doctors told us that Will was paralyzed from the waist down, blind in both eyes, on life support, and in a coma. We sat at his bedside just praying and crying.',
    },
    {
      date: 'April 15, 2023',
      title: 'First signs of improvement',
      snippet: 'Will begins breathing without full ventilator support',
      fullText:
        'Will remained in the ICU for the next 63 days. The nursing staff, doctors and aids worked around the clock and were, to say the least, amazing. Every day Will had someone at his side; Mom, Dad or another family member. We were glued to the monitors, watching his heart rate, oxygen level, blood pressure, temperature and ventilator. His condition would literally change each hour and the team would administer whatever was necessary to keep him stable. At one point, Will had twelve tubes in him for various needs. The Neurological team soon came to the conclusion that this was some type of autoimmune neurological disease, something they had suspected but were not sure of. Thankfully, they had started the medical protocol as soon as he arrived. Each day we came and hoped for any improvement, and soon enough, we saw some. On April 15th, Will started to breathe without the full support of the ventilator, which was a huge breakthrough.',
    },
    {
      date: 'May 10, 2023',
      title: 'Condition improves',
      snippet: 'Downgraded from critical to stable',
      fullText:
        'On May 10th, he was downgraded, which in a hospital is a good thing; from critical to stable. At that time his vision had improved in his right eye, so he was able to see blurred objects and shadows, and it seemed to be improving a little each day. The chest tubes had been removed as well as some other ports in his neck for plasma exchanges and medication. The biggest news was that he was breathing on his own most of the time, although still attached to the ventilator.',
    },
    {
      date: 'May 11, 2023',
      title: 'Transfer to Acute Care',
      snippet: 'Will moves to Acute Care Center',
      fullText:
        'Will was then transferred to the Acute Care Center on May 11th. He continued to improve and the next goal was to get him off the ventilator and onto a trach collar. A trach collar is a small cap that goes over the trach opening which allows oxygen to be inhaled through the trach tube. It then allows the throat and vocal cords to heal, especially from having been intubated for so long. The next big step would be for him not to need the trach collar as he would be breathing totally on his own, and to put a speaking valve on the trach tube which would allow him to speak. We arrived on Mothers Day and the nurse said "I have a surprise for you." She gently put her finger on the trach tube and said to Will, "Is there anything you\'d like to tell your mom?" and Will said "I love you Mom."',
    },
    {
      date: 'June 1, 2023',
      title: 'Rehabilitation begins',
      snippet: 'Will moves to Burke Rehabilitation Center',
      fullText:
        "Will remained in the Acute Care Center until June 1st. During this time he continued to improve. A side note, one day right after he arrived at the Acute Care Center, one of the doctors came and told us Will would probably have the trach tube in for up to a year, a week later the trach tube was removed and Will was eating pizza. That doctor obviously didn't realize who he was dealing with. Will was also starting to gain weight from a low of 125 lbs in the ICU. Now that he was talking he immediately began to start being himself again, joking with the nurses, aides and doctors. It was now time for the next step; patient rehabilitation. On June 1st Will was transported to the Burke Rehabilitation Center in White Plains, NY.",
    },
    {
      date: 'July 19, 2023',
      title: 'Will comes home',
      snippet: 'Finally returns home after months in hospitals',
      fullText:
        "Will still had a long road ahead of him, as he was still paralyzed from the lower chest down and had developed a pressure ulcer on his lower back. The vision in his right eye was improving slowly, however he still had no vision in his left eye. Burke Rehabilitation Center is an amazing place, they have all the latest technology for rehabilitation, and an amazing staff of doctors, nurses, and physical and occupational therapists. Will remained at Burke until July 19th. Unfortunately, due to a bone infection from the pressure ulcer which necessitated a PICC line (a long thin tube that's inserted through a vein in your arm and passed through to the larger veins near your heart), this would limit his ability for upper body rehab, and the decision was made for him to come home.",
    },
    {
      date: 'July 23, 2023',
      title: 'Setback',
      snippet: 'Develops infection, returns to hospital',
      fullText:
        'Unfortunately, after only being home 4 days and finally feeling like things were getting somewhat normal, his catheter line clogged and he was spiking a 102 degree fever. We went to Eastern Long Island Hospital in Greenport praying for a quick solution. The ER doctor advised us that test results revealed an infection and he had ordered an ambulance to transport Will to Stony Brook University Hospital. We were devastated, we being Debbie and Andy. Will, of course, was the positive one. When we arrived at Stony Brook, Will was evaluated and put on strong IV antibiotics and we were advised Will may be there for a week or more. That would mean missing the tournament. By the grace of God his temperature came down and his vitals improved quickly.',
    },
    {
      date: 'July 27, 2023',
      title: 'Returns home again',
      snippet: 'Released from hospital with home IV antibiotics',
      fullText:
        'Late Thursday afternoon the doctor said Will could go home with us doing the IV antibiotics. No need to tell us twice, so Thursday night we came home.',
    },
    {
      date: 'July 29, 2023',
      title: "Billy's Will Power Volleyball Tournament",
      snippet: "Community fundraiser to support Will's needs",
      fullText:
        'Now, you may wonder why is it called Billy\'s Will Power Volleyball Tournament. Good question. For clarification, Will was born Willam Andrew Hickox. Growing up he was always referred to as "Billy" , However when Billy went to college he thought "Billy" was a bit well, "Billy". And so at the age of 18 he thought "Will" would be more mature. As parents, Debbie and Andy reflect on the most amazing days of their lives. The day we got married (from what we remember), the birth of all three of our amazing children, Charles, Melissa, and William, and Billy\'s Will Power Volleyball Tournament. We had been on a roller coaster for 146 days straight, and on Saturday, we pulled to a stop and got off to smell the roses. What we witnessed was indeed a miracle in itself. Over 100 volleyball players, young and old (well, old-er) having the time of their life, hundreds and hundreds of people arriving all day enjoying watching the tournament, bidding on over 100 Chinese auction prizes, all donated by local businesses and families from the community, and some from companies that aren\'t local but got word and wanted to help. Hundreds of volunteers gave their time and talents to make this an event that will remain in our hearts as a memento of love. As we watched the day unfold, we remember being almost numb with emotion. To say the least, we were overwhelmed. We think of that day often and recollect on the emotions we felt, best off all was the smile on Will\'s face, all day.',
    },
    {
      date: 'January 7, 2025',
      title: 'Suffolk Times Sports Person of the Year',
      snippet: 'Will receives prestigious award',
      fullText: (
        <>
          The Suffolk Times has awarded William Hickox with the{' '}
          <Link
            className="text-blue-500"
            href="https://suffolktimes.timesreview.com/2025/01/2024-sportsperson-of-the-year-billy-hickox/"
            target="_blank"
          >
            “2024 Sports Person of the Year”
          </Link>
          and the Will Power Trust team are incredibly proud of this award. This is such a great
          honor as this shows the effect he and the Will Power Trust team have had on the community
          and inspires the team to continue on this path. Sports in the North Fork community are
          more than just competition, they are a way to bring those of the area together to support
          one another and make them better. We look forward to improving our community through
          sport, service and community outreach as others have supported us in this journey.
        </>
      ),
    },
  ];

  return (
    <div className="relative py-8 px-4 sm:px-0">
      {/* Timeline line */}
      <motion.div
        className="absolute left-[1.3rem] sm:left-1/2 top-0 bottom-0 w-px bg-slate-200"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 1, ease: 'easeInOut' }}
      />

      {/* Timeline events */}
      <div className="space-y-8 sm:space-y-16">
        {timelineEvents.map((event, index) => (
          <TimelineItem
            key={event.date}
            event={event}
            index={index}
            isExpanded={expandedIndex === index}
            toggleExpand={() => toggleExpand(index)}
          />
        ))}
      </div>
    </div>
  );
}

function TimelineItem({
  event,
  index,
  isExpanded,
  toggleExpand,
}: Readonly<{
  event: TimelineEvent;
  index: number;
  isExpanded: boolean;
  toggleExpand: () => void;
}>) {
  const ref = useRef(null);
  const isEven = index % 2 === 0;

  // Simple fade-in animation for the timeline item
  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.1,
        ease: 'easeOut',
      },
    },
  };

  // Shared transition for smooth animations
  const transition = {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    duration: 0.3,
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeInVariants}
      className={cn(
        'relative flex items-start',
        'sm:justify-start',
        isEven ? 'sm:justify-end' : ''
      )}
    >
      {/* Date marker */}
      <motion.div
        layout
        transition={transition}
        className="absolute left-2.5 sm:left-1/2 -ml-2.5 h-4 w-4 rounded-full bg-blue-500 border-4 border-white shadow-sm z-10"
      />

      {/* Content */}
      <motion.div
        layout
        transition={transition}
        onClick={() => !isExpanded && toggleExpand()}
        className={cn(
          'ml-1.5 sm:ml-0',
          'w-[calc(100%-3rem)] sm:w-[calc(50%-2rem)]',
          'px-4 sm:px-6 py-4 rounded-lg border border-slate-200 bg-white shadow-sm',
          isEven ? 'sm:mr-8' : 'sm:ml-8',
          !isExpanded && 'cursor-pointer hover:border-blue-200 hover:shadow-md transition-all'
        )}
      >
        <motion.h3
          layout
          transition={transition}
          className={cn(
            'text-lg sm:text-xl font-semibold mb-1',
            'text-left',
            isEven ? 'sm:text-right' : 'sm:text-left'
          )}
        >
          {event.date}
        </motion.h3>

        <motion.h4
          layout
          transition={transition}
          className={cn(
            'text-base sm:text-lg font-medium text-slate-700',
            'text-left',
            isEven ? 'sm:text-right' : 'sm:text-left'
          )}
        >
          {event.title}
        </motion.h4>

        <motion.div
          layout
          transition={transition}
          className={cn(
            'mt-2 overflow-hidden',
            'text-left',
            isEven ? 'sm:text-right' : 'sm:text-left'
          )}
        >
          <AnimatePresence mode="wait">
            {isExpanded ? (
              <motion.div
                key="expanded"
                initial={{ height: 0 }}
                animate={{
                  height: 'auto',
                  transition: {
                    height: { duration: 0.3, ease: 'easeOut' },
                  },
                }}
                exit={{
                  height: 0,
                  transition: {
                    height: { duration: 0.3, ease: 'easeIn' },
                  },
                }}
                className="text-slate-600 text-sm sm:text-base"
              >
                <p className="mb-4">{event.fullText}</p>
                <div
                  className={cn(
                    'flex',
                    'justify-start',
                    isEven ? 'sm:justify-end' : 'sm:justify-start'
                  )}
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={e => {
                      e.stopPropagation();
                      toggleExpand();
                    }}
                    className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    <ChevronUp size={16} />
                    <span>Show less</span>
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="collapsed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-2"
              >
                <p className="text-slate-500 text-sm sm:text-base">{event.snippet}</p>
                <div
                  className={cn(
                    'flex',
                    'justify-start',
                    isEven ? 'sm:justify-end' : 'sm:justify-start'
                  )}
                >
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={e => {
                      e.stopPropagation();
                      toggleExpand();
                    }}
                    className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 transition-colors"
                  >
                    <ChevronDown size={16} />
                    <span>Read more</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
